/**
 * Ticket Management on the merged People Tickets table. Rendered by TicketManagementView once the
 * proxy's pilot gate lets this person in; everyone else still gets the BP Tickets list.
 *
 * Queue tabs open on "My queues" — the queues where Ticket Routing names this person — because
 * three teams now share one list and each mostly works its own. Everyone with Ticket Management
 * access can still switch to any queue: covering a colleague is the reason that access is broad.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsList, TabsTrigger } from '@finn-auto/design-system/atoms/tabs';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Button } from '@finn-auto/design-system/atoms/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { fetchPeopleTicketOptions, fetchPeopleTickets, updatePeopleTicket } from '../../services/peopleTicketsApi';
import type { PeopleTicket, PeopleTicketsAccess, TicketPerson } from '../../types/peopleTickets';
import { CategoryBadge, PriorityBadge } from '../../components/ticketing/badges';
import { daysAgo, nameFromEmail } from '../../components/ticketing/format';
import { MultiSelect } from '../../components/ticketing/MultiSelect';
import { StatusSelect } from '../../components/ticketing/StatusSelect';
import { PeopleTicketDialog } from '../../components/peopleTickets/PeopleTicketDialog';
import {
  ALL_TICKETS,
  MY_TICKETS,
  OPEN_STATUSES,
  type QueueTab,
  defaultQueueTab,
  groupByAssignee,
  inTab,
  queueLabel,
} from '../../components/peopleTickets/ticketList';

const TICKETS_KEY = ['people-tickets'];

/**
 * One radius and one type scale on every control (David, Oct 1 2026): 4px corners; page title,
 * content at 14px, details at 12px — the same design-system tokens as the request form.
 */
const FILTER = 'rounded border-pearl text-black body-14-regular';

export function PeopleTicketManagement({ access }: { access: PeopleTicketsAccess }) {
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const tickets = useQuery({ queryKey: TICKETS_KEY, queryFn: fetchPeopleTickets });
  const options = useQuery({ queryKey: ['people-tickets-options'], queryFn: fetchPeopleTicketOptions, staleTime: 30 * 60_000 });

  const [tab, setTab] = useState<QueueTab>(defaultQueueTab(access.myQueues));
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set(OPEN_STATUSES));
  const [assigneeFilter, setAssigneeFilter] = useState<Set<string>>(new Set());
  // A Slack notification links to /ticket-management?ticket=<record id>: that ticket opens directly,
  // whatever tab or filter would otherwise hide it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(searchParams.get('ticket'));
  const closeTicket = () => {
    setOpenId(null);
    if (searchParams.has('ticket')) setSearchParams({}, { replace: true });
  };

  const all = useMemo(() => tickets.data ?? [], [tickets.data]);

  // Routing's people plus anyone already holding a ticket (someone reassigned by hand in Airtable).
  const assigneeOptions: TicketPerson[] = useMemo(
    () =>
      [
        ...new Map(
          [...(access.assignees ?? []), ...all.flatMap((t) => (t.assignee ? [t.assignee] : []))].map((p) => [p.email, p]),
        ).values(),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [all, access.assignees],
  );

  const groups = useMemo(
    () =>
      groupByAssignee(
        all.filter(
          (t) =>
            inTab(t, tab, myEmail) &&
            (statusFilter.size === 0 || statusFilter.has(t.status)) &&
            (assigneeFilter.size === 0 || assigneeFilter.has(t.assignee?.email ?? '')),
        ),
        myEmail,
      ),
    [all, tab, statusFilter, assigneeFilter, myEmail],
  );

  /** Optimistic, then the server's answer — which carries what the proxy stamped (Resolved At). */
  async function change(ticket: PeopleTicket, optimistic: Partial<PeopleTicket>, request: () => Promise<PeopleTicket>) {
    const previous = queryClient.getQueryData<PeopleTicket[]>(TICKETS_KEY);
    queryClient.setQueryData<PeopleTicket[]>(TICKETS_KEY, (list) => list?.map((t) => (t.id === ticket.id ? { ...t, ...optimistic } : t)));
    try {
      const updated = await request();
      queryClient.setQueryData<PeopleTicket[]>(TICKETS_KEY, (list) => list?.map((t) => (t.id === ticket.id ? { ...t, ...updated } : t)));
    } catch (err) {
      queryClient.setQueryData(TICKETS_KEY, previous);
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'The change was not saved' });
      throw err;
    }
  }

  const quiet = (p: Promise<unknown>) => p.catch(() => undefined);

  if (tickets.isLoading || options.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" aria-label="Loading tickets" />
      </div>
    );
  }
  if (tickets.isError || options.isError || !options.data) {
    return <p className="p-6 body-14-regular text-red">Tickets could not be loaded. Please reload the page.</p>;
  }

  const openTicket = all.find((t) => t.id === openId) ?? null;
  const tabs: { value: QueueTab; label: string }[] = [
    { value: MY_TICKETS, label: 'My Tickets' },
    { value: ALL_TICKETS, label: 'All Tickets' },
    ...options.data.queues.map((q) => ({ value: q as QueueTab, label: queueLabel(q) })),
  ];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-black">Ticket Overview</h1>
        <div className="flex flex-wrap gap-2">
          <MultiSelect
            label="Assigned to"
            options={[{ value: '', label: 'Unassigned' }, ...assigneeOptions.map((p) => ({ value: p.email, label: p.name }))]}
            selected={assigneeFilter}
            onChange={setAssigneeFilter}
            triggerClassName={FILTER}
          />
          <MultiSelect
            label="Status"
            options={options.data.statuses.map((s) => ({ value: s, label: s }))}
            selected={statusFilter}
            onChange={setStatusFilter}
            triggerClassName={FILTER}
          />
          <Button size="sm" variant="secondary" className="rounded" onClick={() => navigate('/submit-request')}>
            New request
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as QueueTab)}>
        <TabsList className="mb-6 h-auto w-full justify-start gap-1 overflow-x-auto border-b border-solid border-pearl bg-transparent p-0 pb-3">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="whitespace-nowrap rounded px-4 py-3 body-14-semibold">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-6">
        {groups.map((group) => (
          <section key={group.email || 'unassigned'} className="overflow-hidden rounded border border-solid border-pearl bg-white">
            <header className="flex items-center justify-between border-b border-solid border-pearl bg-snow px-4 py-2.5">
              {/* Flex, not inline: a design-system type class on an inline span breaks the line
                  (its trimming ::before is display:table), which put "(you)" under the name. */}
              <h2 className="flex items-baseline gap-1.5">
                <span className="body-14-semibold text-black">{group.name}</span>
                {group.email === myEmail && <span className="body-12-regular text-trustedBlue">(you)</span>}
              </h2>
              <span className="body-12-regular text-iron">
                {group.tickets.length} ticket{group.tickets.length === 1 ? '' : 's'}
              </span>
            </header>
            {/* m-0 p-0: the app's global list styles indent every <ul> by 40px. */}
            <ul className="m-0 list-none divide-y divide-solid divide-pearl p-0">
              {group.tickets.map((t) => (
                <li key={t.id} className="flex cursor-pointer items-end justify-between gap-4 px-4 py-3 hover:bg-snow" onClick={() => setOpenId(t.id)}>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-baseline gap-2">
                      {/* Untrimmed like the title beside it, or the two no longer share a baseline. */}
                      <span className="shrink-0 body-12-regular text-iron before:hidden after:hidden">{t.ticketKey}</span>
                      {/* before/after:hidden: the type class trims the line box to the letter height,
                          and truncate's overflow:hidden then cut off descenders (y, p, g). */}
                      <p className="min-w-0 truncate body-14-semibold text-black before:hidden after:hidden">{t.summary}</p>
                    </div>
                    {/* before/after:hidden: in a flex row the type class's trimming ::before becomes an
                        empty first item, and the gap indented the whole line. */}
                    <div className="flex flex-wrap items-center gap-2 body-12-regular text-iron before:hidden after:hidden">
                      <span>
                        {nameFromEmail(t.submittedBy)} · {t.department || '—'}
                      </span>
                      <CategoryBadge category={t.subcategory ? `${t.category} · ${t.subcategory}` : t.category} />
                      {(tab === ALL_TICKETS || tab === MY_TICKETS) && t.queue && <span>{queueLabel(t.queue)}</span>}
                      <span>Submitted {daysAgo(t.submittedAt)}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <PriorityBadge priority={t.priority} />
                    <StatusSelect status={t.status} onChange={(status) => quiet(change(t, { status }, () => updatePeopleTicket(t.id, { status })))} />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {groups.length === 0 && <p className="body-14-regular text-iron">No tickets match this filter.</p>}
      </div>

      {openTicket && (
        <PeopleTicketDialog
          ticket={openTicket}
          options={options.data}
          assigneeOptions={assigneeOptions}
          onClose={closeTicket}
          onStatusChange={(status) => quiet(change(openTicket, { status }, () => updatePeopleTicket(openTicket.id, { status })))}
          onPriorityChange={(priority) => quiet(change(openTicket, { priority }, () => updatePeopleTicket(openTicket.id, { priority })))}
          onCategoryChange={(queue, category) =>
            quiet(change(openTicket, { queue, category }, () => updatePeopleTicket(openTicket.id, { queue, category })))
          }
          onAssigneeChange={(assignee) =>
            quiet(change(openTicket, { assignee }, () => updatePeopleTicket(openTicket.id, { assigneeEmail: assignee.email })))
          }
          onNoteSave={(field, value) =>
            change(openTicket, { [field]: value }, () =>
              updatePeopleTicket(openTicket.id, field === 'resolutionNote' ? { resolutionNote: value } : { internalNotes: value }),
            ).then(() => undefined)
          }
        />
      )}
    </div>
  );
}
